import { AfterViewInit, ChangeDetectorRef, Component, HostListener, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { SetupScreensService } from '../../services/setup-screens.service';
import { EmailSetup, EmailSetupFiltersResponse, LocationOption } from '../../models/email-setup.model';
import { Pagination } from '../../../product-data/models/response.model';

@Component({
  selector: 'app-email-setup',
  templateUrl: './email-setup.component.html',
  styleUrls: ['./email-setup.component.scss'],
  standalone: false
})
export class EmailSetupComponent implements OnInit, AfterViewInit, CanComponentDeactivate {
  @ViewChild(TableComponent) tableRef: TableComponent<Element>;
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public setupScreensService = inject(SetupScreensService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  emailSetupList: EmailSetup[] = [];
  nameFilterList: string[] = [];
  emailFilterList: string[] = [];
  typeFilterList: string[] = ['Plant', 'Site', 'Customer'];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  hasUnsavedChanges = false;
  locallyAddedRows: EmailSetup[] = [];
  itemToDelete: EmailSetup | null = null;
  private controlMap = new Map<string, Map<string, FormControl>>();

  typeOptions: string[] = ['Plant', 'Site', 'Customer'];

  // Location options per type
  plantOptions: LocationOption[] = [];
  siteOptions: LocationOption[] = [];
  customerOptions: LocationOption[] = [];

  // Per-row location options (keyed by emailSetupPk)
  rowLocationOptions = new Map<string, LocationOption[]>();

  // Track which rows are in edit mode (by emailSetupPk)
  editingRows = new Set<string>();

  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 50, 100];

  tableConfig: TableConfig<EmailSetup> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {
      type: { cell: 'type', header: 'Type' },
      name: { cell: 'name', header: 'Name' },
      email: { cell: 'email', header: 'Email' },
      location: { cell: 'location', header: 'Type' },
      action: { cell: 'action', header: 'Action' }
    }
  };

  fetcher: PaginatorFetcher<EmailSetup> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();

    return this.setupScreensService.getEmailSetups(this.filterFormBackend.value, filters.page, filters.limit).pipe(
      map((response: any) => {
        const { emailSetups = [], pagination } = response.data || {};
        this.emailSetupList = emailSetups.map((row: EmailSetup) => this.normalizeEmailSetup(row));
        this.pagination = pagination;
        const combined = [...this.locallyAddedRows, ...this.emailSetupList];
        this.updateDisplayedRowsForm(combined);
        const totalCount = (pagination?.totalRecords || emailSetups.length) + this.locallyAddedRows.length;
        return {
          items: combined,
          count: totalCount
        };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      names: [[]],
      emails: [[]],
      types: [[]]
    });
    this.filterFormBackend = this.fb.group({
      names: [[]],
      emails: [[]],
      types: [[]]
    });
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      names: this.filterForm.get('names')?.value || '',
      emails: this.filterForm.get('emails')?.value || '',
      types: this.filterForm.get('types')?.value || ''
    });
  }

  ngOnInit(): void {
    this.setupFormListeners();
    this.loadFilters();
    this.loadAllLocationData();
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig = {
        ...this.tableConfig,
        columns: {
          type: {
            cell: 'type',
            header: this.translationService.translate('emailTypeText') || 'Type'
          },
          name: {
            cell: 'name',
            header: this.translationService.translate('nameText') || 'Name'
          },
          email: {
            cell: 'email',
            header: this.translationService.translate('emailText') || 'Email'
          },
          location: {
            cell: 'location',
            header: this.translationService.translate('locationText') || 'Type'
          },
          action: {
            cell: 'action',
            header: this.translationService.translate('actionText') || 'Action'
          }
        }
      };
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    // Trigger initial table fetch after view is fully initialized
    // so the paginator renders correctly on first load
    setTimeout(() => {
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  private loadFilters(): void {
    this.setupScreensService.getEmailSetupFilters().subscribe((response: EmailSetupFiltersResponse) => {
      this.nameFilterList = response.data.names || [];
      this.emailFilterList = response.data.emails || [];
    });
  }

  private loadAllLocationData(): void {
    // Load plants — value=id (PK), label=code - name
    this.setupScreensService.getPlants().subscribe({
      next: (response: any) => {
        const plants = response?.data || [];
        this.plantOptions = plants.map((p: any) => ({
          value: p.id,
          label: `${p.code} - ${p.name}`
        }));
      },
      error: () => {
        this.plantOptions = [];
      }
    });

    // Load sites — value=siteSetupPk or id (PK), label=code - name
    this.setupScreensService.getAllSitesForDropdown().subscribe({
      next: (response: any) => {
        const sites = response?.data || [];
        this.siteOptions = sites.map((s: any) => ({
          value: s.siteSetupPk || s.id,
          label: `${s.siteCode || s.code} - ${s.siteName || s.name}`
        }));
      },
      error: () => {
        this.siteOptions = [];
      }
    });

    // Load customers — value=customerPk or id (PK), label=code - name
    this.setupScreensService.getAllCustomersForDropdown().subscribe({
      next: (response: any) => {
        const rawData = response?.data;
        const customers = Array.isArray(rawData) ? rawData : rawData?.customers || [];
        this.customerOptions = customers.map((c: any) => ({
          value: c.customerPk || c.id,
          label: `${c.code || c.customerCode} - ${c.customerName || c.name}`
        }));
      },
      error: () => {
        this.customerOptions = [];
      }
    });
  }

  /**
   * Normalize a type value from the backend (e.g. "PLANT", "plant") to title-case
   * so it matches the dropdown options ("Plant", "Site", "Customer").
   */
  normalizeType(type: string | null): string {
    if (!type) return '';
    const normalized = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    return this.typeOptions.includes(normalized) ? normalized : type;
  }

  /**
   * Normalize the type field of an EmailSetup row coming from the backend.
   */
  normalizeEmailSetup(row: EmailSetup): EmailSetup {
    return { ...row, type: this.normalizeType(row.type) };
  }

  getLocationOptionsForType(type: string): LocationOption[] {
    const normalized = type ? type.toLowerCase() : '';
    switch (normalized) {
      case 'plant':
        return [...this.plantOptions];
      case 'site':
        return [...this.siteOptions];
      case 'customer':
        return [...this.customerOptions];
      default:
        return [];
    }
  }

  getLocationOptionsForRow(item: EmailSetup): LocationOption[] {
    const key = item?.emailPk || '';
    return this.rowLocationOptions.get(key) || [];
  }

  onTypeChange(item: EmailSetup): void {
    const key = item?.emailPk || '';
    const fieldMap = this.controlMap.get(key);
    if (!fieldMap) return;
    const type = fieldMap.get('type')?.value;
    const options = this.getLocationOptionsForType(type);
    this.rowLocationOptions.set(key, options);
    // Clear the location value when type changes
    fieldMap.get('location')?.setValue('');
  }

  getLocationLabel(item: EmailSetup): string {
    if (!item.location) return '';
    const allOptions = [...this.plantOptions, ...this.siteOptions, ...this.customerOptions];
    const match = allOptions.find((opt) => opt.value === item.location);
    return match ? match.label : item.location;
  }

  resetFilters(): void {
    this.filterForm.reset();
  }

  private setupFormListeners(): void {
    this.filterForm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      this.generateFilterForm();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  /**
   * Rebuild the form array from items.
   * Only preserve edited values for rows that are currently being edited (in editingRows).
   * This prevents stale edited values from leaking into freshly fetched server data.
   */
  updateDisplayedRowsForm(items: EmailSetup[]): void {
    const currentValues = new Map<string, any>();
    const existingFormArray = this.displayedRowsForm.get('rows') as FormArray;
    if (existingFormArray) {
      for (let i = 0; i < existingFormArray.length; i++) {
        const val = existingFormArray.at(i).value;
        if (val?.emailPk && this.editingRows.has(val.emailPk)) {
          currentValues.set(val.emailPk, val);
        }
      }
    }

    this.controlMap.clear();
    const rowForms = items.map((row) => {
      const editedValues = currentValues.get(row.emailPk);
      const rowData = editedValues ? { ...row, ...editedValues } : row;
      const group = this.createRowForm(rowData);
      const key = row.emailPk || '';
      const fieldMap = new Map<string, FormControl>();
      fieldMap.set('emailPk', group.get('emailPk') as FormControl);
      fieldMap.set('type', group.get('type') as FormControl);
      fieldMap.set('name', group.get('name') as FormControl);
      fieldMap.set('email', group.get('email') as FormControl);
      fieldMap.set('location', group.get('location') as FormControl);
      this.controlMap.set(key, fieldMap);

      // Set location options based on current type (use a fresh copy per row)
      const typeVal = editedValues ? editedValues.type : row.type;
      if (typeVal) {
        this.rowLocationOptions.set(key, this.getLocationOptionsForType(typeVal));
      }

      return group;
    });
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  createRowForm(row: EmailSetup): FormGroup {
    return this.fb.group({
      emailPk: [row.emailPk],
      type: [row.type, [Validators.required]],
      name: [row.name, [Validators.required]],
      email: [row.email, [Validators.required, Validators.email]],
      location: [row.location]
    });
  }

  /**
   * Get form control for a specific item and field.
   * Always uses controlMap keyed by emailSetupPk — no index-based fallback
   * to avoid cross-row contamination.
   */
  getControl(item: EmailSetup, controlName: keyof EmailSetup): FormControl {
    const key = item?.emailPk || '';
    const fieldMap = this.controlMap.get(key);
    if (fieldMap) {
      const control = fieldMap.get(controlName as string);
      if (control) {
        return control;
      }
    }
    // Return a detached control as a safe fallback — will not affect other rows
    return new FormControl('');
  }

  get canEdit(): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return roles.includes('PRODUCT_SETUP_EDIT');
  }

  get canView(): boolean {
    const roles: string = localStorage.getItem('roles') || '';
    return roles.includes('PRODUCT_SETUP_VIEW');
  }

  isEditing(item: EmailSetup): boolean {
    return this.editingRows.has(item.emailPk);
  }

  isNewRow(item: EmailSetup): boolean {
    return this.locallyAddedRows.some((r) => r.emailPk === item.emailPk);
  }

  startEdit(item: EmailSetup): void {
    const key = item.emailPk || '';
    this.editingRows.add(item.emailPk);
    // Pre-populate location options for the current type (fresh copy)
    if (item.type) {
      this.rowLocationOptions.set(key, this.getLocationOptionsForType(item.type));
    }
    this.hasUnsavedChanges = true;
  }

  cancelEdit(item: EmailSetup): void {
    if (this.isNewRow(item)) {
      this.locallyAddedRows = this.locallyAddedRows.filter((r) => r.emailPk !== item.emailPk);
      this.editingRows.delete(item.emailPk);
      this.rowLocationOptions.delete(item.emailPk);
      this.updateDisplayedRowsForm([...this.locallyAddedRows, ...this.emailSetupList]);
      this.tableRef.resetFilters();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    } else {
      const original = this.emailSetupList.find((r) => r.emailPk === item.emailPk);
      if (original) {
        const key = item.emailPk || '';
        const fieldMap = this.controlMap.get(key);
        if (fieldMap) {
          fieldMap.get('type')?.setValue(original.type);
          fieldMap.get('name')?.setValue(original.name);
          fieldMap.get('email')?.setValue(original.email);
          fieldMap.get('location')?.setValue(original.location);
        }
        // Restore location options for the original type
        if (original.type) {
          this.rowLocationOptions.set(key, this.getLocationOptionsForType(original.type));
        }
      }
      this.editingRows.delete(item.emailPk);
    }
    this.hasUnsavedChanges = this.editingRows.size > 0;
  }

  saveRow(item: EmailSetup): void {
    if (!this.canEdit) {
      return;
    }

    const key = item.emailPk || '';
    const fieldMap = this.controlMap.get(key);
    if (!fieldMap) {
      return;
    }

    const type = fieldMap.get('type')?.value;
    const name = fieldMap.get('name')?.value;
    const email = fieldMap.get('email')?.value;
    const location = fieldMap.get('location')?.value;

    if (!type) {
      this.alertService.showAlert({
        type: AlertType.critical,
        content: this.translationService.translate('emailSetupTypeRequiredText') || 'Type is required.'
      } as Alert);
      return;
    }

    if (!name) {
      this.alertService.showAlert({
        type: AlertType.critical,
        content: this.translationService.translate('emailSetupNameRequiredText') || 'Name is required.'
      } as Alert);
      return;
    }

    if (!email) {
      this.alertService.showAlert({
        type: AlertType.critical,
        content: this.translationService.translate('emailSetupEmailRequiredText') || 'Email is required.'
      } as Alert);
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      this.alertService.showAlert({
        type: AlertType.critical,
        content: this.translationService.translate('invalidEmailText') || 'Please enter a valid email address.'
      } as Alert);
      return;
    }

    // Duplicate name check across existing and locally added rows (excluding the current row)
    const allRows = [...this.emailSetupList, ...this.locallyAddedRows];
    const normalizedName = name.trim().toLowerCase();
    const isDuplicate = allRows.some((row) => row.emailPk !== item.emailPk && row.name?.trim().toLowerCase() === normalizedName);
    // Also check edited (in-flight) rows via controlMap
    const isDuplicateInEditing = Array.from(this.controlMap.entries()).some(([pk, fieldMap]) => {
      if (pk === key) return false;
      const editedName = fieldMap.get('name')?.value;
      return editedName?.trim().toLowerCase() === name.trim().toLowerCase();
    });

    if (isDuplicate || isDuplicateInEditing) {
      this.alertService.showAlert({
        type: AlertType.critical,
        content:
          this.translationService.translate('duplicateEmailNameText') ||
          'An email setup with this name already exists. Please enter a unique name.'
      } as Alert);
      return;
    }

    const payload: EmailSetup[] = [
      {
        emailPk: item.emailPk,
        type,
        name,
        email,
        location
      }
    ];

    this.setupScreensService.updateEmailSetup(payload).subscribe({
      next: () => {
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('emailSetupSavedText') || 'Email setup saved successfully'
        } as Alert);

        // Remove from editing and locally-added BEFORE re-fetching
        this.editingRows.delete(item.emailPk);
        this.rowLocationOptions.delete(item.emailPk);
        this.locallyAddedRows = this.locallyAddedRows.filter((r) => r.emailPk !== item.emailPk);
        this.hasUnsavedChanges = this.editingRows.size > 0;
        this.loadFilters();
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('emailSetupSaveErrorText') || 'Error encountered while saving email setup'
        } as Alert);
      }
    });
  }

  addRow(): void {
    this.setupScreensService.getNewEmailSetupPk().subscribe({
      next: (response: any) => {
        const newId = response?.data;
        const newRow: EmailSetup = {
          emailPk: newId,
          type: '',
          name: '',
          email: '',
          location: ''
        };
        this.locallyAddedRows.push(newRow);
        this.editingRows.add(newId);
        this.hasUnsavedChanges = true;
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText') || 'An error occurred'
        } as Alert);
      }
    });
  }

  deleteRow(item: EmailSetup): void {
    const locallyAddedIndex = this.locallyAddedRows.findIndex((e) => e.emailPk === item.emailPk);

    if (locallyAddedIndex !== -1) {
      this.locallyAddedRows.splice(locallyAddedIndex, 1);
      this.editingRows.delete(item.emailPk);
      this.rowLocationOptions.delete(item.emailPk);

      this.alertService.showAlert({
        type: AlertType.success,
        content: this.translationService.translate('emailSetupDeleteText') || 'Email setup deleted successfully'
      } as Alert);

      this.updateDisplayedRowsForm([...this.locallyAddedRows, ...this.emailSetupList]);
      this.tableRef.resetFilters();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      this.hasUnsavedChanges = this.editingRows.size > 0;
      return;
    }

    const payload = item.emailPk;
    this.setupScreensService.deleteEmailSetup(payload).subscribe({
      next: () => {
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('emailSetupDeleteText') || 'Email setup deleted successfully'
        } as Alert);
        this.emailSetupList = this.emailSetupList.filter((row) => row.emailPk !== item.emailPk);
        this.editingRows.delete(item.emailPk);
        this.rowLocationOptions.delete(item.emailPk);
        this.updateDisplayedRowsForm([...this.locallyAddedRows, ...this.emailSetupList]);
        this.loadFilters();
        this.resetFilters();
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.hasUnsavedChanges = this.editingRows.size > 0;
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('emailSetupDeleteErrorText') || 'Error encountered while deleting the email setup'
        } as Alert);
      }
    });
  }

  showDeleteModal(item: EmailSetup): void {
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText') || 'Warning!',
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText') || 'Yes',
      cancel: this.translationService.translate('noText') || 'No'
    });
    this.itemToDelete = item;
  }

  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.deleteRow(this.itemToDelete);
      } else {
        this.itemToDelete = null;
      }
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText') || 'Discard Changes',
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText') || 'OK',
      cancel: this.translationService.translate('cancelText') || 'Cancel'
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.locallyAddedRows = [];
        this.editingRows.clear();
        this.rowLocationOptions.clear();
        this.updateDisplayedRowsForm(this.emailSetupList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
        this.hasUnsavedChanges = false;
      }
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.hasUnsavedChanges) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.hasUnsavedChanges) {
      $event.returnValue = this.translationService.translate('discardChangesText');
    }
  }
}
